import React from 'react';
import { FetchResult } from '@apollo/client';
import Linkify from 'react-linkify';

import Icon from '@buyside/components/presentation/Icon/icon';
import Like from '@buyside/components/presentation/Feed/Actions/like';
import Link from '@buyside/components/presentation/Link/link';
import MessageBubbleIcon from '@buyside/svgs/message_bubble.svg';

import { LikeEventMutation, Maybe, Post, useLikeEventMutation } from '@interfaces/graphql-typings';

import { truncateText } from '@buyside/util/textHelpers';
import { postUrl } from '@buyside/util/urlHelper';

import styles from '@buyside/components/presentation/IdeaShowPage/RightSidebar/RelatedPosts/authorPostsStyles.scss';

const AuthorPost: React.FC<{
  id: string;
  body?: Maybe<string>;
  commentsCount?: Maybe<number>;
  currentUserHasLiked: boolean;
  eventId?: Maybe<string>;
  likeCount: number;
  expanded: boolean;
}> = ({ id, body, commentsCount, currentUserHasLiked, eventId, likeCount, expanded }) => {
  const [showFull, toggleShowFull] = React.useState(expanded);

  const [likePost] = useLikeEventMutation({
    update(cache, { data }) {
      const hasLiked = Boolean(data?.likeEvent?.event?.currentUserHasLiked);

      cache.modify<Post>({
        id: cache.identify({ id: id, __typename: 'Post' }),
        fields: {
          currentUserHasLiked: () => hasLiked,
          likeCount: cachedLikeCount => (hasLiked ? cachedLikeCount + 1 : cachedLikeCount - 1)
        }
      });
    }
  });

  return (
    <>
      <div className={styles.post}>
        <Linkify
          componentDecorator={(href, text, key): React.ReactElement => (
            <a href={href} key={key} target="_blank" rel="noopener noreferrer">
              {text}
            </a>
          )}
        >
          {expanded || showFull ? (
            <p onClick={(): void => toggleShowFull(false)}>{body}</p>
          ) : (
            <p data-testid={`post-body-${id}`} onClick={(): void => toggleShowFull(true)}>
              {truncateText(body, 80)}
            </p>
          )}
        </Linkify>
      </div>
      <div className={styles.userEngagement}>
        {eventId && (
          <Like
            currentUserHasLiked={Boolean(currentUserHasLiked)}
            id={`post-like-${id}`}
            likeCount={likeCount}
            onClick={(): Promise<FetchResult<LikeEventMutation>> =>
              likePost({ variables: { eventId: eventId } })
            }
          />
        )}
        <Link to={postUrl(id)}>
          <Icon type={MessageBubbleIcon} style={styles.bubble} />
          {commentsCount || 0} Comments
        </Link>
        <Link to={postUrl(id)}>View More {'>>'}</Link>
      </div>
    </>
  );
};

export default AuthorPost;
